<template>
  <div class="min-h-screen bg-white text-gray-900 font-sans selection:bg-[#0d47a1] selection:text-white">
    <!-- Header -->
    <header class="max-w-screen-2xl mx-auto px-8 h-24 flex items-center justify-between">
      <div class="flex items-center space-x-3">
        <div class="h-16 w-16 bg-[#0d47a1] rounded flex items-center justify-center text-white">
          <i class="material-icons text-2xl">calculate</i>
        </div>
        <span class="text-2xl font-black uppercase ">DIYPayroll</span>
      </div>
      <div class="flex items-center space-x-6">
        <template v-if="is_authenticated">
          <router-link :to="dashboard_link" class="text-base font-black uppercase tracking-wide text-[#0d47a1] transition-colors">Dashboard</router-link>
          <button @click="logout" class="bg-gray-100 text-gray-500 text-base font-black uppercase tracking-wide px-6 py-2.5 rounded-lg hover:bg-gray-200 transition">Logout</button>
        </template>
        <template v-else>
          <router-link to="/login" class="text-base font-black uppercase tracking-wide text-gray-500 hover:text-[#0d47a1] transition-colors">Login</router-link>
          <router-link to="/register" class="bg-[#0d47a1] text-white text-base font-black uppercase tracking-wide px-6 py-2.5 rounded-lg hover:bg-blue-800 transition shadow-sm">Get Started</router-link>
        </template>
      </div>
    </header>

    <!-- Content -->
    <main class="max-w-screen-2xl mx-auto px-8 pt-20 pb-32 relative">
      <DoodleBackground />

      <!-- Straight Glassy Metric Card (Bluer & More Transparent) -->
      <div class="absolute top-10 right-10 lg:right-20 p-8 bg-blue-200/30 backdrop-blur-2xl rounded-3xl border border-blue-200/40 shadow-2xl z-20 animate__animated animate__fadeIn">
        <p class="text-base font-black uppercase tracking-[0.2em] text-[#0d47a1]/70 mb-2">Total Volume</p>
        <span class="text-4xl font-black text-[#0d47a1] ">$500K+</span>
        <p class="text-base font-bold text-[#0d47a1]/60 uppercase mt-2 border-t border-blue-300/30 pt-2">USD Processed</p>
      </div>

      <div class="max-w-3xl animate__animated animate__fadeIn relative z-10">
        <h1 class="text-6xl font-black leading-[1.05] tracking-tight mb-12">
          The professional environment for <span class="text-[#0d47a1]">organizational payroll.</span>
        </h1>
        <p class="text-3xl text-gray-500 font-medium leading-relaxed mb-12">
          A secure workspace for managing employee pay, tax compliance, and financial reporting without the unnecessary complexity.
        </p>
        <router-link to="/register" class="inline-block px-10 py-4 bg-[#0d47a1] text-white text-lg font-black uppercase tracking-wide rounded-lg shadow-lg hover:bg-blue-800 transition transform hover:-translate-y-0.5 active:translate-y-0">Create Your Free Account</router-link>
      </div>

      <!-- Functional Overview -->
      <div class="mt-32 grid grid-cols-1 lg:grid-cols-2 gap-20 border-t border-gray-100 pt-20 relative z-10">
        <div>
          <h2 class="text-base font-black text-[#0d47a1] uppercase tracking-[0.3em] mb-14">How it works</h2>
          <div class="space-y-12">
            <div>
              <h3 class="text-2xl font-black uppercase tracking-tight mb-2">Internal Ledger Controls</h3>
              <p class="text-lg text-gray-500 leading-relaxed">Map payroll categories directly to your Chart of Accounts. Use template strings to automatically create sub-accounts for each employee.</p>
            </div>
            <div>
              <h3 class="text-2xl font-black uppercase tracking-tight mb-2">Automated Tax Withholding</h3>
              <p class="text-lg text-gray-500 leading-relaxed">Taxes are calculated automatically based on where you work. Manage state rates and employer costs in one place.</p>
            </div>
            <div>
              <h3 class="text-2xl font-black uppercase tracking-tight mb-2">Employee Records</h3>
              <p class="text-lg text-gray-500 leading-relaxed">Keep a complete record for every employee. Get instant access to past paystubs, yearly earnings, and timesheet logs.</p>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-3xl border border-gray-100 shadow-sm p-2 overflow-hidden">
          <div class="bg-gray-50 rounded-2xl h-full w-full min-h-[400px] flex items-center justify-center p-12">
            <div class="w-full space-y-6 opacity-40 grayscale">
              <div class="h-4 bg-gray-200 rounded w-1/4"></div>
              <div class="grid grid-cols-3 gap-4">
                <div class="h-24 bg-gray-200 rounded-xl"></div>
                <div class="h-24 bg-gray-200 rounded-xl"></div>
                <div class="h-24 bg-gray-200 rounded-xl"></div>
              </div>
              <div class="space-y-3">
                <div class="h-2 bg-gray-200 rounded w-full"></div>
                <div class="h-2 bg-gray-200 rounded w-5/6"></div>
                <div class="h-2 bg-gray-200 rounded w-4/6"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Footer -->
    <footer class="max-w-screen-2xl mx-auto px-8 py-12 border-t border-gray-100 flex flex-col md:flex-row items-center justify-between text-base font-black uppercase tracking-wide text-gray-400">
      <div class="flex items-center space-x-2 text-gray-900">
        <div class="h-4 w-4 bg-[#0d47a1] rounded flex items-center justify-center text-white"><i class="material-icons text-base">calculate</i></div>
        <span>DIYPayroll © 2026</span>
      </div>
      <div class="flex space-x-8 mt-6 md:mt-0">
        <router-link to="/docs" class="hover:text-[#0d47a1]">Documentation</router-link>
        <router-link to="/privacy" class="hover:text-[#0d47a1]">Privacy</router-link>
        <router-link to="/terms" class="hover:text-[#0d47a1]">Internal Terms</router-link>
        <a href="mailto:help@diypayroll.io" class="hover:text-[#0d47a1]">help@diypayroll.io</a>
      </div>
    </footer>
  </div>
</template>

<script>
import get_api from "../service/api.js"
import base_url from "../service/base_url.js"
import DoodleBackground from '../components/DoodleBackground.vue'

export default {
  name: 'LandingPage',
  components: {
    DoodleBackground
  },
  data() {
    const token = localStorage.getItem('XPAYROLLaccess_token');
    const isValidToken = token && token !== 'undefined' && token !== 'null' && token.trim() !== '';
    return {
      is_authenticated: !!isValidToken,
      dashboard_link: '/login'
    }
  },
  created() {
    const token = localStorage.getItem('XPAYROLLaccess_token')
    if (token && token !== 'undefined' && token !== 'null' && token.trim() !== '') {
      const api = get_api(this.$router)
      api.get(base_url + "/me").then((resp) => {
        const user = resp.data.data
        if (user.is_admin) {
          this.dashboard_link = "/dashboard"
        } else {
          this.dashboard_link = "/employee/" + user.id
        }
      }).catch(() => {
        this.is_authenticated = false
        const currentToken = localStorage.getItem('XPAYROLLaccess_token')
        if (currentToken === token) {
          localStorage.removeItem('XPAYROLLaccess_token')
        }
      })
    }
  },
  methods: {
    logout() {
      localStorage.removeItem('XPAYROLLaccess_token')
      this.is_authenticated = false
      window.location.reload()
    }
  }
}
</script>

<style scoped>
.animate__animated.animate__fadeIn {
  --animate-duration: 1s;
}
</style>